/**
 * Copyright Higharc Inc. 2019
 *
 * A class encapsulating a render target and associated render pipeline passes for drawing. Separate render targets can be used to simultaneously manage on-screen and offscreen rendering.
 * @module
 */
import { assertIsNotUndefined } from "common/utils/assertOfType";
import hashString from "common/utils/hashString";
import { RandomSourceWithSeed } from "common/utils/random";
import type { AABB2 } from "geom/aabb2Types";
import * as THREE from "three";
import { defaultOrthoCameraSettings } from "../../CameraSettings";
import { cameraToTHREE } from "../../convert";
import type { HighlightDataTexture } from "../../HighlightDataTexture";
import { pickingMaterial } from "../../materials/pickingMaterial";
import { highlightOnlyFragmentShader, highlightOnlyVertexShader } from "../../materials/shaders/highlightOnly";
import type { WebGLExtendedCapabilities } from "../../WebGLExtendedCapabilities";
import type { EffectComposer } from "../examples/jsm/postprocessing/EffectComposer";
import { effectComposerRender } from "../examples/jsm/postprocessing/EffectComposer";
import type { EffectComposerPass } from "../examples/jsm/postprocessing/EffectComposerPass";
import { RenderPass } from "../examples/jsm/postprocessing/RenderPass";
import { AlternativeMaterialPass } from "./AlternativeMaterialPass";
import { AOAlgorithm } from "./AOAlgorithm";
import { BlurShaderUtils2 } from "./DepthLimitedBlurShader2";
import { DepthNormalPass } from "./DepthNormalPass";
import { GTAOPass } from "./GTAOPass";
import { HatchMaterialPass } from "./HatchMaterialPass";
import { LineworkPass } from "./LineworkPass";
import {
  MaterialSharedParameterGroup,
  SOFT_SHADOW_CAMERA_FAR_DEFAULT,
  type SharedUniformValues,
} from "./MaterialSharedParameterGroup";
import { RenderPipelineParamsContainer } from "./RenderPipelineParamsContainer";
import type { RenderTargetPool } from "./RenderTargetPool";
import { SAOPass2 } from "./SAOPass2";
import { ScreenSpaceShadowPass, ScreenSpaceShadowPassOutput } from "./ScreenSpaceShadowPass";
import { LightProbeVisualizationMode } from "./SHLightProbeConstants";
import { SSAAPass } from "./SSAAPass";
import { identityColorLUT, uvCalibrationMaterial } from "./SSFXRendererGlobalResources";
import { SSFXRendererOutput } from "./SSFXRendererOutput";
import type { RendererParams } from "./SSFXRendererParams";
import {
  applyMaterialSharedParameterGroupDefines,
  defaultLightProbeRenderParams,
  defaultLineworkParams,
  type ScreenRenderParams,
} from "./SSFXRendererParams";
import type SSFXRendererScene from "./SSFXRendererScene";
import type { LightProbeRenderParams, LineworkParams } from "./SSFXRendererTypes";
import { SSRRPass } from "./SSRRPass";
import { TransmissionVisualization } from "./TransmissionVisualization";

// Ensure that the depth/normal render target size is evenly divisible by two - that simplifies half-resolution AO rendering.
const DEFAULT_AO_SIZE_GRANULARITY = 2.0;

function isAncestor(child: THREE.Object3D, parent: THREE.Object3D): boolean {
  if (child === parent) {
    return true;
  }
  return child.parent ? isAncestor(child.parent, parent) : false;
}

export class SSFXRenderTarget {
  private readonly whiteTexture: THREE.DataTexture;

  readonly renderPipelineParams: RenderPipelineParamsContainer;
  readonly lightProbeRenderParams: LightProbeRenderParams;
  readonly lineworkParams: LineworkParams;

  private renderPass: RenderPass;
  private highlightPass: RenderPass;
  private overlayPasses: EffectComposerPass[] = [];

  private highlightOnlyMaterial: THREE.ShaderMaterial;

  depthNormalPass: DepthNormalPass;
  saoPass: SAOPass2;
  gtaoPass: GTAOPass;
  ssaaPass: SSAAPass;
  ssrrPass: SSRRPass;
  screenSpaceShadowPass: ScreenSpaceShadowPass;
  hatchMaterialPass: HatchMaterialPass;
  alternativeMaterialPass: AlternativeMaterialPass;
  lineworkPass: LineworkPass;

  private renderOneSampleComposer: EffectComposer;

  private camera: THREE.Camera = cameraToTHREE(defaultOrthoCameraSettings, 1);
  private scene: SSFXRendererScene | undefined;

  private shaderColorGradingLUT: THREE.Texture | undefined;

  private randomSource: RandomSourceWithSeed;

  private transmissionVisualization: TransmissionVisualization;

  // Keeps track of which version of the scene has been used to last update the alternative material mapping.
  private alternativeMaterialGeneration = -1;
  private alternativeMaterialSceneId = -1;
  private hatchMaterialGeneration = -1;
  private hatchMaterialSceneId = -1;

  sampleRenderTargetWidth = 0;
  sampleRenderTargetHeight = 0;

  constructor(
    private renderer: THREE.WebGLRenderer,
    private renderTargetPool: RenderTargetPool,
    private finalRenderWidth: number,
    private finalRenderHeight: number,
    capabilities: WebGLExtendedCapabilities,
  ) {
    this.randomSource = new RandomSourceWithSeed(0);

    this.transmissionVisualization = new TransmissionVisualization();

    this.whiteTexture = new THREE.DataTexture(
      new Uint8Array([255, 255, 255, 255]),
      1,
      1,
      THREE.RGBAFormat,
      THREE.UnsignedByteType,
    );
    this.whiteTexture.needsUpdate = true;

    this.renderPipelineParams = new RenderPipelineParamsContainer(this, capabilities);

    this.lineworkParams = {
      ...defaultLineworkParams,
    };

    this.lightProbeRenderParams = {
      ...defaultLightProbeRenderParams,
    };

    this.depthNormalPass = new DepthNormalPass(
      this.scene?.scene,
      this.camera,
      capabilities.defaultDepthTextureType,
      undefined,
      DEFAULT_AO_SIZE_GRANULARITY,
    );
    this.depthNormalPass.params.enableViewZConversion = capabilities.defaultEnableViewZConversion;

    this.depthNormalPass.onAfterPass = () => {
      MaterialSharedParameterGroup.instance.setAoMapDimensions({
        aoMapPixelOffset: this.depthNormalPass.paddedPixelOffset,
        aoMapSize: this.depthNormalPass.paddedSize,
      });

      this.lineworkPass.uniforms["depthMapPixelOffset"].value = this.depthNormalPass.paddedPixelOffset;
      this.lineworkPass.uniforms["depthMapInvSize"].value.set(
        1.0 / this.depthNormalPass.paddedSize.x,
        1.0 / this.depthNormalPass.paddedSize.y,
      );
    };

    this.saoPass = new SAOPass2(this.camera, this.depthNormalPass, undefined);

    this.gtaoPass = new GTAOPass(this.camera, this.depthNormalPass, undefined);

    this.ssrrPass = new SSRRPass(this.scene?.scene, this.camera, this.depthNormalPass, {
      colorSrcColorSpace: THREE.SRGBColorSpace,
    });
    this.ssrrPass.output = SSRRPass.OUTPUT["ReflectionBuffer"];

    this.screenSpaceShadowPass = new ScreenSpaceShadowPass(this.scene?.scene, this.camera, this.depthNormalPass);

    // Composer for rendering single frames / SSAA samples.
    this.renderOneSampleComposer = [];

    this.renderOneSampleComposer.push(this.depthNormalPass);

    this.renderOneSampleComposer.push(this.saoPass);

    this.renderOneSampleComposer.push(this.gtaoPass);

    this.renderOneSampleComposer.push(this.ssrrPass);

    this.renderOneSampleComposer.push(this.screenSpaceShadowPass);

    this.renderPass = new RenderPass(this.scene?.scene, this.camera, undefined, new THREE.Color(1.0, 1.0, 1.0), 1.0);
    this.renderOneSampleComposer.push(this.renderPass);

    this.highlightOnlyMaterial = new THREE.ShaderMaterial({
      vertexShader: highlightOnlyVertexShader,
      fragmentShader: highlightOnlyFragmentShader,
      clipping: true,
      name: "SSFXRenderTarget highlightOnlyMaterial",
    });
    MaterialSharedParameterGroup.instance.useSharedUniformsInMaterial(this.highlightOnlyMaterial.uniforms);
    const lineworkCompositingComposer: EffectComposer = [];

    this.highlightPass = new RenderPass(
      this.scene?.scene,
      this.camera,
      this.highlightOnlyMaterial,
      new THREE.Color(1.0, 1.0, 1.0),
      1.0,
    );
    lineworkCompositingComposer.push(this.highlightPass);
    this.highlightPass.enabled = false;

    this.hatchMaterialPass = new HatchMaterialPass(this.scene, this.camera);
    lineworkCompositingComposer.push(this.hatchMaterialPass);
    this.hatchMaterialPass.enabled = false;

    this.alternativeMaterialPass = new AlternativeMaterialPass(this.scene?.scene, this.camera);
    this.renderOneSampleComposer.push(this.alternativeMaterialPass);
    this.alternativeMaterialPass.enabled = false;

    this.lineworkPass = new LineworkPass(
      this.camera,
      this.depthNormalPass,
      this.alternativeMaterialPass,
      this.renderTargetPool,
    );
    this.lineworkPass.childEffects = lineworkCompositingComposer;
    this.renderOneSampleComposer.push(this.lineworkPass);
    this.lineworkPass.enabled = false;

    this.renderOneSampleComposer.push(this.overlayPasses);

    const callbackBeforeRenderSample = (sampleIndex: number) => {
      MaterialSharedParameterGroup.instance.randomSeed = Math.round(this.randomSource.next() * 1024) / 1024;
      MaterialSharedParameterGroup.instance.sampleIndex = sampleIndex;
      this.gtaoPass.randomSeed = this.randomSource.next();
      this.saoPass.randomSeed = this.randomSource.next();
      this.ssrrPass.randomSeed = this.randomSource.next();
      this.screenSpaceShadowPass.randomSeed = this.randomSource.next();
    };

    const callbackBeforeOutput = (uniforms, _material) => {
      uniforms["colorGradingLUT"].value = this.shaderColorGradingLUT;
    };

    // TODO: Loading the LUT might be subject to color space conversion by the browser. Make sure this is handled as intended!
    // Note that we can't use ImageBitmap as it isn't supported by all browsers yet.
    // We might need to load the LUT from a data array to ensure consistent behaviour.
    const outputShaderCustomization = {
      adjustColorParams: "#include <color_grading_lut_params>",
      adjustColor: "gl_FragColor = gradeColor(gl_FragColor);",
      uniforms: {
        colorGradingLUT: { type: "t", value: null },
      },
    };

    this.ssaaPass = new SSAAPass(
      this.renderer,
      this.camera as THREE.PerspectiveCamera | THREE.OrthographicCamera,
      this.renderPipelineParams.useHalfFloatLinearRenderBuffer,
      this.renderOneSampleComposer,
      undefined,
      callbackBeforeRenderSample,
      callbackBeforeOutput,
      outputShaderCustomization,
      this.depthNormalPass,
    );
  }

  private updateIntermediateRenderTargetSizes(renderResolutionMultiplier: number): boolean {
    const sampleRenderTargetWidth = Math.floor(this.finalRenderWidth * renderResolutionMultiplier);
    const sampleRenderTargetHeight = Math.floor(this.finalRenderHeight * renderResolutionMultiplier);
    if (
      this.sampleRenderTargetWidth !== sampleRenderTargetWidth ||
      this.sampleRenderTargetHeight !== sampleRenderTargetHeight
    ) {
      this.sampleRenderTargetWidth = sampleRenderTargetWidth;
      this.sampleRenderTargetHeight = sampleRenderTargetHeight;
      this.ssaaPass.setSize(sampleRenderTargetWidth, sampleRenderTargetHeight);
      this.ssaaPass.resetAccumulation();
      return true;
    }
    return false;
  }

  private updateParamsInRenderTarget(container: any, params: any) {
    for (const key of Object.keys(params)) {
      container[key] = params[key];
    }
  }

  applyRenderParams(renderParams: RendererParams) {
    this.updateParamsInRenderTarget(this.renderPipelineParams, renderParams.renderPipelineParams);
    this.updateParamsInRenderTarget(this.lineworkParams, renderParams.lineworkParams);
    this.updateParamsInRenderTarget(this.depthNormalPass.params, renderParams.depthNormalParams);
    this.updateParamsInRenderTarget(this.saoPass.params, renderParams.saoParams);
    this.updateParamsInRenderTarget(this.ssaaPass, renderParams.dofParams);
    this.updateParamsInRenderTarget(this.gtaoPass.params, renderParams.gtaoParams);
    this.updateParamsInRenderTarget(this.ssrrPass, renderParams.reflectionParams);
    this.updateParamsInRenderTarget(this.screenSpaceShadowPass, renderParams.screenSpaceShadowParams);
    this.updateParamsInRenderTarget(this.lightProbeRenderParams, renderParams.lightProbeRenderParams);

    applyMaterialSharedParameterGroupDefines(renderParams.softShadowParams);
  }

  private setupRenderPipelineForFrame() {
    const toneMapping: THREE.ToneMapping = parseInt(this.renderPipelineParams.toneMapping as any) as THREE.ToneMapping;
    MaterialSharedParameterGroup.instance.sharedDefines.toneMapping = toneMapping;
    this.ssaaPass.toneMapping = toneMapping;

    const aoPass = this.renderPipelineParams.aoAlgorithm === AOAlgorithm.SAO ? this.saoPass : this.gtaoPass;

    const outputLinework =
      this.renderPipelineParams.output === SSFXRendererOutput.LINEWORK ||
      this.renderPipelineParams.output === SSFXRendererOutput.LINEWORK_TRANSPARENT ||
      this.renderPipelineParams.output === SSFXRendererOutput.LINEWORK_WITH_SELECTION;

    const outputFinalOrTransmission =
      this.renderPipelineParams.output === SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS ||
      this.renderPipelineParams.output === SSFXRendererOutput.TRANSMISSION_VISUALIZATION;

    const outputLight =
      this.renderPipelineParams.output === SSFXRendererOutput.LIGHT_PROBE_IRRADIANCE ||
      this.renderPipelineParams.output === SSFXRendererOutput.LIGHT ||
      this.renderPipelineParams.output === SSFXRendererOutput.LIGHT_WITHOUT_DETAIL;

    this.ssaaPass.currentSampleMinWeight = outputLinework ? 1 / 7 : 1 / 6;

    const displayingReflections = this.renderPipelineParams["reflectionsEnabled"] && outputFinalOrTransmission;
    MaterialSharedParameterGroup.instance.sharedDefines.ssrEnabled = displayingReflections
      ? true
      : MaterialSharedParameterGroup.instance.sharedDefines.ssrEnabled && !outputFinalOrTransmission;
    this.ssrrPass.enabled = displayingReflections;

    const outputFromShadowPass = this.renderPipelineParams["output"] === SSFXRendererOutput.SCREEN_SPACE_SHADOWS;

    const displayingScreenSpaceShadows =
      this.renderPipelineParams.screenSpaceShadowsEnabled && (outputFinalOrTransmission || outputLight);
    const displayingSmallScaleAO =
      this.renderPipelineParams.smallScaleAOEnabled && (outputFinalOrTransmission || outputLight);
    this.screenSpaceShadowPass.enabled = displayingScreenSpaceShadows || displayingSmallScaleAO || outputFromShadowPass;
    this.screenSpaceShadowPass.shadowsEnabled = displayingScreenSpaceShadows;
    this.screenSpaceShadowPass.aoEnabled = displayingSmallScaleAO;
    MaterialSharedParameterGroup.instance.sharedDefines.screenSpaceShadowsEnabled = displayingScreenSpaceShadows;
    MaterialSharedParameterGroup.instance.sharedDefines.smallScaleAOEnabled = displayingSmallScaleAO;

    assertIsNotUndefined(this.scene);

    this.scene.getDirectionalLightDirection(this.screenSpaceShadowPass.lightDirection);
    this.screenSpaceShadowPass.lightDirection.transformDirection(this.camera.matrixWorldInverse);
    this.screenSpaceShadowPass.output = outputFromShadowPass
      ? ScreenSpaceShadowPassOutput.WriteToScreen
      : ScreenSpaceShadowPassOutput.ShadowBuffer;

    const needsDepthNormalPassForEffects =
      this.renderPipelineParams.aoAlgorithm !== AOAlgorithm.NONE ||
      displayingReflections ||
      displayingScreenSpaceShadows ||
      displayingSmallScaleAO;

    this.depthNormalPass.enabled =
      (needsDepthNormalPassForEffects &&
        this.renderPipelineParams.output !== SSFXRendererOutput.PICKING_IDS &&
        this.renderPipelineParams.output !== SSFXRendererOutput.UV_CALIBRATION) ||
      this.renderPipelineParams["output"] === SSFXRendererOutput.NORMAL ||
      this.ssaaPass.reprojectionTAA ||
      outputLinework;

    this.depthNormalPass.params.renderNormalTexture =
      (this.renderPipelineParams.aoAlgorithm !== AOAlgorithm.NONE && aoPass.needsNormalTexture()) ||
      displayingReflections ||
      displayingScreenSpaceShadows ||
      displayingSmallScaleAO ||
      this.renderPipelineParams["output"] === SSFXRendererOutput.NORMAL ||
      outputLinework;

    this.depthNormalPass.params.outputEnabled =
      this.renderPipelineParams["output"] === SSFXRendererOutput.NORMAL ||
      this.renderPipelineParams["output"] === SSFXRendererOutput.DEPTH ||
      (this.renderPipelineParams["output"] >= SSFXRendererOutput.DEPTH_MIP_1 &&
        this.renderPipelineParams["output"] <= SSFXRendererOutput.DEPTH_MIP_10);
    if (this.depthNormalPass.enabled && this.depthNormalPass.params.outputEnabled) {
      this.depthNormalPass.params.output = this.renderPipelineParams.output;
    }
    const outputPhysicalMaterial =
      outputFinalOrTransmission || outputLight || this.renderPipelineParams["output"] === SSFXRendererOutput.BASE_COLOR;

    this.saoPass.enabled = false;
    this.gtaoPass.enabled = false;
    this.gtaoPass.worldUpVector.copy(this.scene.worldUpVector);
    aoPass.enabled =
      this.renderPipelineParams.aoAlgorithm !== AOAlgorithm.NONE &&
      !this.depthNormalPass.params.outputEnabled &&
      !outputFromShadowPass &&
      !outputLinework &&
      this.renderPipelineParams.output !== SSFXRendererOutput.PICKING_IDS &&
      this.renderPipelineParams.output !== SSFXRendererOutput.UV_CALIBRATION &&
      this.renderPipelineParams.output !== SSFXRendererOutput.MATERIAL_ID;
    if (aoPass.enabled) {
      if (
        this.renderPipelineParams.output === SSFXRendererOutput.NORMAL_FOR_AO ||
        this.renderPipelineParams.output === SSFXRendererOutput.AO
      ) {
        aoPass.params.output = this.renderPipelineParams.output;
      } else {
        aoPass.params.output = SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS;
      }
    }

    this.renderPass.clearAlpha = this.renderPipelineParams.transparentBackground ? 0.0 : 1.0;

    this.renderPass.enabled =
      outputPhysicalMaterial ||
      this.renderPipelineParams.output === SSFXRendererOutput.PICKING_IDS ||
      this.renderPipelineParams.output === SSFXRendererOutput.UV_CALIBRATION ||
      this.renderPipelineParams.output === SSFXRendererOutput.MESH_ID;

    this.renderPass.overrideMaterial = undefined;
    if (this.renderPipelineParams.output === SSFXRendererOutput.PICKING_IDS) {
      this.renderPass.overrideMaterial = pickingMaterial;
    } else if (this.renderPipelineParams.output === SSFXRendererOutput.UV_CALIBRATION) {
      this.renderPass.overrideMaterial = uvCalibrationMaterial();
    }

    // We need to refresh shared uniforms even if we're rendering linework with selection,
    // because the highlight data texture is passed in as a shared uniform.
    if (outputPhysicalMaterial || this.renderPipelineParams.output === SSFXRendererOutput.LINEWORK_WITH_SELECTION) {
      const useHalfFloatLinearRenderBuffer = this.renderPipelineParams.useHalfFloatLinearRenderBuffer;
      this.scene.applyToneMappingToSkyMaterial(useHalfFloatLinearRenderBuffer, toneMapping);
      const sceneSharedUniformValues = this.scene.getSceneSharedUniformValues(useHalfFloatLinearRenderBuffer);

      const sharedUniformValues: SharedUniformValues = {
        ...sceneSharedUniformValues,

        outputType: this.renderPipelineParams.output,
        ssfxEnabled: true,

        screenSpaceAoMap:
          this.renderPipelineParams.aoAlgorithm === AOAlgorithm.NONE ? this.whiteTexture : aoPass.screenSpaceAoMap,
        screenSpaceReflectionMap: this.ssrrPass.reflectionBuffer.texture,
        screenSpaceShadowMap: this.screenSpaceShadowPass.shadowBuffer.texture,
        renderTargetResolution: [this.sampleRenderTargetWidth, this.sampleRenderTargetHeight],

        probeGridNormalOffsetMultiplier: this.lightProbeRenderParams.probeGridNormalOffsetMultiplier,
        probeGridSmoothBackfaceWeightConstantTerm:
          this.lightProbeRenderParams.probeGridSmoothBackfaceWeightConstantTerm,
        lightProbeIrradianceMultiplier: this.lightProbeRenderParams.lightProbeIrradianceMultiplier,
        substitutedReflectionMultiplier: this.renderPipelineParams.substitutedReflectionMultiplier,

        softShadowCameraFar: SOFT_SHADOW_CAMERA_FAR_DEFAULT,
      };
      MaterialSharedParameterGroup.instance.setSharedUniformValues(sharedUniformValues);

      MaterialSharedParameterGroup.instance.sharedDefines.isolateModeSupported =
        this.renderPipelineParams.isolateModeSupported;
    }

    const outputLineworkWithSelection =
      this.renderPipelineParams["output"] === SSFXRendererOutput.LINEWORK_WITH_SELECTION;
    this.highlightPass.enabled = outputLineworkWithSelection;

    this.hatchMaterialPass.enabled = outputLinework && this.lineworkParams.hatchMaterialsEnabled;
    if (this.hatchMaterialPass.enabled) {
      this.hatchMaterialPass.clear = !outputLineworkWithSelection;
      if (
        !this.scene.container ||
        this.hatchMaterialSceneId !== this.scene.scene.id ||
        this.hatchMaterialGeneration !== this.scene.container.generation
      ) {
        this.hatchMaterialPass.updateScene(this.lineworkParams.hatchDistanceThreshold);
        this.hatchMaterialSceneId = this.scene.scene.id;
        this.hatchMaterialGeneration = this.scene.container ? this.scene.container.generation : -1;
      }
    }

    this.alternativeMaterialPass.enabled =
      outputLinework || this.renderPipelineParams.output === SSFXRendererOutput.MATERIAL_ID;
    this.alternativeMaterialPass.outputToScreen = this.renderPipelineParams.output === SSFXRendererOutput.MATERIAL_ID;
    if (
      this.alternativeMaterialPass.enabled &&
      (!this.scene.container ||
        this.alternativeMaterialSceneId !== this.scene.scene.id ||
        this.alternativeMaterialGeneration !== this.scene.container.generation)
    ) {
      this.alternativeMaterialPass.updateScene();
      this.alternativeMaterialSceneId = this.scene.scene.id;
      this.alternativeMaterialGeneration = this.scene.container ? this.scene.container.generation : -1;
    }

    this.lineworkPass.enabled = outputLinework;
    if (this.lineworkPass.enabled) {
      this.lineworkPass.uniforms["renderInvSize"].value.set(
        1.0 / this.sampleRenderTargetWidth,
        1.0 / this.sampleRenderTargetHeight,
      );
      this.lineworkPass.uniforms["normalDotProductThreshold"].value = BlurShaderUtils2.normalDotProductCutoffFromAngle(
        this.lineworkParams["lineworkNormalAngleThreshold"],
      );
      this.lineworkPass.uniforms["depthThreshold"].value = this.lineworkParams["lineworkDepthThreshold"];
      this.depthNormalPass.setSizeGranularity(1.0);
      this.lineworkPass.transparent = this.renderPipelineParams.output === SSFXRendererOutput.LINEWORK_TRANSPARENT;
    } else {
      this.depthNormalPass.setSizeGranularity(DEFAULT_AO_SIZE_GRANULARITY);
    }

    if (this.renderPipelineParams.includeOverlayInOutput && this.scene.container) {
      this.scene.container.setupOverlayPasses(this.overlayPasses, this.camera);
    } else {
      this.overlayPasses.length = 0;
    }

    this.ssaaPass.toneMappingEnabled = this.scene.sceneLightsManager.toneMappingEnabled;
    this.ssaaPass.toneMappingExposure = this.scene.sceneLightsManager.toneMappingExposure;
    this.ssaaPass.useHalfFloatLinearRenderBuffer = this.renderPipelineParams.useHalfFloatLinearRenderBuffer;
    let colorGradingLUT = this.scene.colorGradingLUT;
    if (colorGradingLUT === undefined || colorGradingLUT === null) {
      colorGradingLUT = identityColorLUT;
    }
    this.shaderColorGradingLUT = colorGradingLUT;
    this.shaderColorGradingLUT.minFilter = THREE.LinearFilter;
    this.shaderColorGradingLUT.magFilter = THREE.LinearFilter;

    this.scene.isRenderingWireframe = MaterialSharedParameterGroup.instance.sharedDefines.wireframe;

    const lightProbeGrids = this.scene.lightProbeGrids;
    for (let i = 0, len = lightProbeGrids.length; i < len; ++i) {
      lightProbeGrids[i].visualizationMode = parseInt(this.lightProbeRenderParams.lightProbeVisualization as any);
      const gridVisible = this.lightProbeRenderParams.lightProbeVisualization !== LightProbeVisualizationMode.None;
      lightProbeGrids[i].visible = gridVisible;
      if (gridVisible) {
        lightProbeGrids[i].updateMatrixWorld();
      }
    }

    this.transmissionVisualization.update(
      this.renderPipelineParams.output === SSFXRendererOutput.TRANSMISSION_VISUALIZATION,
      this.scene.scene,
      this.camera,
    );

    this.setupMeshDebugOverrideMaterial();
  }

  /**
   * Set materials for individual meshes when needed for certain types of debug visualization.
   */
  private setupMeshDebugOverrideMaterial() {
    assertIsNotUndefined(this.scene);
    const needMeshIdMaterial = this.renderPipelineParams.output === SSFXRendererOutput.MESH_ID;
    if (needMeshIdMaterial) {
      this.scene.scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          const staticContent = this.scene?.container?.staticContent;
          if (staticContent && isAncestor(obj, staticContent)) {
            return;
          }
          if (!obj.material.userData.materialParams) {
            // Not a design material, skip
            return;
          }
          if (obj.userData.materialOverriddenByDebugMode) {
            return; // Already using mesh id material
          }
          obj.userData.materialOverriddenByDebugMode = obj.material;
          const hash = hashString(obj.uuid);
          // Use different parts of the 32-bit hash for hue, saturation, and lightness
          const hue = (hash & 0xffff) / 0xffff; // 16 bits for hue
          // Avoiding low values for saturation and lightness so that the colors are more distinct.
          const saturation = 0.5 + (((hash >>> 16) & 0xff) / 0xff) * 0.5; // 8 bits for saturation.
          const lightness = 0.35 + (((hash >>> 24) & 0x7f) / 0x7f) * 0.4; // 7 bits for lightness
          const color = new THREE.Color();
          color.setHSL(hue, saturation, lightness);
          obj.material = new THREE.MeshBasicMaterial({ color, fog: false });
        }
      });
      this.scene.usingMeshIdMaterial = true;
    } else if (this.scene.usingMeshIdMaterial) {
      this.scene.scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          if (!obj.userData.materialOverriddenByDebugMode) {
            return;
          }
          obj.material = obj.userData.materialOverriddenByDebugMode;
          delete obj.userData.materialOverriddenByDebugMode;
        }
      });
      this.scene.usingMeshIdMaterial = false;
    }
  }

  setScene(scene: SSFXRendererScene) {
    if (scene !== this.scene) {
      this.ssaaPass.resetAccumulation();
      this.alternativeMaterialGeneration = -1;
    }
    this.scene = scene;
    this.depthNormalPass.scene = scene.scene;
    this.ssrrPass.scene = scene.scene;
    this.screenSpaceShadowPass.scene = scene.scene;
    this.renderPass.scene = scene.scene;
    this.hatchMaterialPass.scene = scene;
    this.alternativeMaterialPass.scene = scene.scene;
    this.highlightPass.scene = scene.scene;
  }

  setCamera(camera: THREE.Camera) {
    if (camera !== this.camera) {
      this.ssaaPass.resetAccumulation();
    }
    this.camera = camera;
    this.depthNormalPass.camera = camera;
    this.saoPass.camera = camera;
    this.gtaoPass.camera = camera;
    this.renderPass.camera = camera;
    this.hatchMaterialPass.camera = camera;
    this.alternativeMaterialPass.camera = camera;
    this.highlightPass.camera = camera;
    this.ssrrPass.camera = camera;
    this.screenSpaceShadowPass.camera = camera;
    this.ssaaPass.camera = camera as THREE.PerspectiveCamera | THREE.OrthographicCamera;
    this.lineworkPass.camera = camera;
  }

  setFinalRenderSize(finalRenderWidth: number, finalRenderHeight: number) {
    this.finalRenderWidth = finalRenderWidth;
    this.finalRenderHeight = finalRenderHeight;
  }

  // Returns final render resolution in actual pixels. Pixel ratio is handled outside of SSFXRenderer.
  getFinalRenderSize(): { width: number; height: number } {
    return { width: this.finalRenderWidth, height: this.finalRenderHeight };
  }

  beginOffscreenFrame(ssaaSampleLevel: number) {
    this.updateIntermediateRenderTargetSizes(1.0);
    this.randomSource.reset(0);
    this.ssaaPass.sampleLevel = ssaaSampleLevel;
    this.ssaaPass.reprojectionTAA = false;
    this.ssaaPass.resetAccumulation();
  }

  beginFrame({
    ssaaEnabled,
    ssaaTemporalSampleLevel,
    reprojectionTAAEnabled,
    renderResolutionMultiplier,
  }: Pick<
    ScreenRenderParams,
    "ssaaEnabled" | "ssaaTemporalSampleLevel" | "reprojectionTAAEnabled" | "renderResolutionMultiplier"
  >): { didUpdateRenderResolution: boolean } {
    const didUpdateRenderResolution = this.updateIntermediateRenderTargetSizes(renderResolutionMultiplier);
    if (ssaaEnabled) {
      this.depthNormalPass.storePrevViewZRenderTarget = true;
      this.ssaaPass.sampleLevel = ssaaTemporalSampleLevel;
      this.ssaaPass.reprojectionTAA = reprojectionTAAEnabled;
      this.ssaaPass.beginFrame();
    } else {
      this.depthNormalPass.storePrevViewZRenderTarget = false;
      MaterialSharedParameterGroup.instance.randomSeed = 0.5;
      this.saoPass.randomSeed = 0.5;
      this.gtaoPass.randomSeed = 0.5;
      this.ssrrPass.randomSeed = 0.5;
      this.screenSpaceShadowPass.randomSeed = 0.5;
    }
    return { didUpdateRenderResolution };
  }

  isDoneAccumulatingSamples() {
    return this.ssaaPass.isDoneAccumulatingSamples();
  }

  setHighlightDataTexture(highlightDataTexture?: HighlightDataTexture) {
    this.depthNormalPass.setHighlightDataTexture(highlightDataTexture);
  }

  accumulateSampleIfNeeded() {
    this.setupRenderPipelineForFrame();
    this.ssaaPass.accumulateSampleIfNeeded(this.renderer);
  }

  renderSSAAOutput(renderTarget: THREE.WebGLRenderTarget | null, aabb?: AABB2) {
    this.ssaaPass.renderOutput(this.renderer, renderTarget, aabb);
  }

  renderOneSample(renderTarget: THREE.WebGLRenderTarget | null) {
    this.setupRenderPipelineForFrame();
    effectComposerRender(this.renderOneSampleComposer, this.renderer, renderTarget);
  }

  dispose() {
    this.depthNormalPass.dispose();
    this.saoPass.dispose();
    this.gtaoPass.dispose();
    this.ssrrPass.dispose();
    this.lineworkPass.dispose();
    this.alternativeMaterialPass.dispose();
    this.hatchMaterialPass.dispose();
    this.ssaaPass.dispose();
  }
}
